import React from "react";
import { Icon, LogoMark, todoUrgentCount, useSupportAdmin } from "./registry";
import { roleOf, can, roleLabel } from "./lib/roles";
import { VERTICALS, enabledVerticals, canSwitch, routeHidden, writeLensPref } from "./lib/verticals";   // the service switcher (docs/SERVICE_SWITCHER_PLAN.md §6)
/* ============================================================
   Fence Works — collapsible left sidebar navigation
   ============================================================ */

/* Navigation IA — the way a contractor thinks about the day:
     Overview    Dashboard · To-Do
     Sales       Customers · Estimates · Messages · Import & export
     Operations  Work Orders · Schedule · Purchase Orders · Inventory · Crew Mode
   Those three ride flat under small section labels — no extra click before
   Estimates or Work Orders. The less-frequent areas are expandable groups
   (one open at a time): Marketing, Catalog, AI Front Desk, Reports & Finance,
   Tools. Settings and Help & Guide sit at the bottom as their own row (Settings
   is one destination with its own internal tabs). "New estimate" is the one
   primary action above the list. FW Admin is the platform owner's only. */
const SIDEBAR_NAV = [
  { heading: "Overview" },
  { id: "dashboard", label: "Dashboard", icon: "dashboard", cap: "jobs", also: ["tv"] },
  { id: "todo", label: "To-Do", icon: "check", todoBadge: true, cap: "jobs" },
  { heading: "Sales" },
  { id: "customers", label: "Customers", icon: "user", unreadDot: true, cap: "customers", also: ["hub"] },
  { id: "estimates", label: "Estimates", icon: "send", cap: "estimates", also: ["quote"] },
  { id: "messages", label: "Messages", icon: "mail", unreadDot: true, cap: "messages" },
  // bring customers / jobs / a price book in from another app, take any list
  // out as CSV — every plan, it's how people switch
  { id: "dataio", label: "Import & export", icon: "download", cap: "customers" },
  { heading: "Operations" },
  { id: "workorders", label: "Work Orders", icon: "clipboard", cap: "jobs" },
  { id: "calendar", label: "Schedule", icon: "calendar", cap: "jobs" },
  { id: "purchaseorders", label: "Purchase Orders", icon: "box", cap: "purchasing" },
  { id: "inventory", label: "Inventory", icon: "layers", cap: "purchasing" },
  { id: "crew", label: "Crew Mode", icon: "bolt", cap: "field" },
  // ---- winning more work ----
  { group: "marketing", label: "Marketing", icon: "target", cap: "marketing", children: [
    { id: "website", label: "Website", icon: "building" },
    { id: "leadradar", label: "Lead Radar", icon: "target" },
    { id: "reviews", label: "Reviews", icon: "chat" },
    { id: "gbp", label: "Google Profile", icon: "mappin" },
    { id: "widget", label: "Estimator Widget", icon: "map" },
    { id: "widget", section: "visual", label: "Yard Preview Widget", icon: "image", fenceOnly: true },
    { id: "sequences", label: "Email Sequences", icon: "mail" },
    { id: "printkit", label: "Print Kit", icon: "printer" },
  ] },
  // ---- what you sell ----
  { group: "catalog", label: "Catalog", icon: "box", cap: "pricebook", children: [
    { id: "products", label: "Products & Pricing", icon: "box", cap: "pricebook" },
    { id: "specs", label: "Specs", icon: "ruler" },
    { id: "models", label: "3D Models", icon: "layers" },
  ] },
  { group: "ai", label: "AI Front Desk", icon: "phone", cap: "marketing", children: [
    { id: "ai", label: "Agents", icon: "bolt" },
    { id: "ai", section: "settings", label: "Settings", icon: "gear" },
    { id: "phone", label: "Business line", icon: "phone", cap: "integrations" },
  ] },
  { group: "finance", label: "Reports & Finance", icon: "activity", cap: "reports", children: [
    { id: "reports", label: "Reports", icon: "activity" },
    { id: "salesteam", label: "Sales Team", icon: "user" },
    { id: "business", label: "Budget & Cash", icon: "dollar" },
    { id: "financing", label: "Financing", icon: "dollar", cap: "marketing" },
  ] },
  // ---- fence-nerd tools ----
  { group: "tools", label: "Tools", icon: "ruler", children: [
    { id: "visualizer", label: "Visualizer", icon: "image" },
    { id: "codes", label: "Codes", icon: "book" },
    { id: "permits", label: "Permit Check", icon: "mappin", beta: true },
  ] },
];

/* the bottom row: one Settings destination (its own tabs inside: business
   profile, markup rules, payments & tax, crews, customer experience, data &
   integrations) and the guide. Integrations and Job Packet Sheets are
   Settings pages too, so they light this up. */
const SIDEBAR_BOTTOM = [
  { id: "settings", label: "Settings", icon: "gear", cap: "settings", also: ["integrations", "jobpacket", "product-edit"] },
  { id: "guide", label: "Help & Guide", icon: "help" },
];

/* FW ADMIN — the platform owner's own pages (every account's help chats, the
   service's health and recovery queues, account-deletion requests, print
   orders). Shown only to SUPPORT_OWNER_EMAILS (the server tells the help
   bubble's poller, which fans it out here). Kept as its own group at the
   bottom, visually set apart, so nothing here ever reads as a customer
   feature — and so the owner's day-to-day nav stays identical to a customer's. */
const FW_ADMIN_GROUP = {
  group: "fwadmin", label: "FW Admin", icon: "lock", admin: true, children: [
    { id: "fwstatus", label: "Status & To-do", icon: "clipboard" },
    { id: "fwcustomers", label: "Customers", icon: "user" },
    { id: "lifecycle", label: "Lifecycle Emails", icon: "mail" },
    { id: "growth", label: "Growth", icon: "target" },
    { id: "leadengine", label: "Lead Engine", icon: "target", also: ["leadengine-find", "leadengine-today", "leadengine-outreach", "prospect"] },
    { id: "support", label: "Support Inbox", icon: "chat", supportBadge: true },
    { id: "ops", label: "Ops & Health", icon: "activity" },
    { id: "deletions", label: "Deletion Requests", icon: "trash" },
    { id: "printadmin", label: "Print Orders", icon: "printer" },
    { id: "fwlines", label: "Phone lines", icon: "phone" },
    { id: "swag", label: "Swag & Print Partners", icon: "box" },
    { id: "fwadmins", label: "Admins", icon: "lock" },
  ],
};

/* which top-level group owns a given route */
const ROUTE_GROUP = {
  website: "marketing", leadradar: "marketing", reviews: "marketing", gbp: "marketing", widget: "marketing", sequences: "marketing", printkit: "marketing",
  products: "catalog", ratecard: "catalog", specs: "catalog", models: "catalog",
  ai: "ai", automation: "ai", phone: "ai",
  reports: "finance", business: "finance", salesteam: "finance", financing: "finance",
  visualizer: "tools", codes: "tools", permits: "tools",
  fwcustomers: "fwadmin", lifecycle: "fwadmin", growth: "fwadmin", support: "fwadmin", ops: "fwadmin", deletions: "fwadmin", printadmin: "fwadmin", swag: "fwadmin", fwadmins: "fwadmin", fwstatus: "fwadmin", fwlines: "fwadmin",
  leadengine: "fwadmin", "leadengine-find": "fwadmin", "leadengine-today": "fwadmin", "leadengine-outreach": "fwadmin", prospect: "fwadmin",
};

function Sidebar({ route, section, go, auth, store, unread, mobileOpen, setMobileOpen }: any) {
  // the REAL plan (synced from the Stripe-backed subscription) — never hardcoded
  const planId = (store && store.settings && store.settings.plan) || "free";
  const planLabel = ({ free: "Free plan", starter: "Starter plan", pro: "Pro plan", growth: "Growth plan", enterprise: "Growth plan" })[planId] || "Free plan";
  const [collapsed, setCollapsed] = React.useState<any>(() => localStorage.getItem("fw_sidebar_collapsed") === "1");
  const [isMobile, setIsMobile] = React.useState<any>(() => window.innerWidth <= 860);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 860);
    window.addEventListener("resize", h); return () => window.removeEventListener("resize", h);
  }, []);
  const eff = collapsed && !isMobile;
  // accordion: at most one group open — the one that owns the active route,
  // or the one just tapped. Opening one closes the rest, so the nav never
  // grows into a scroll.
  const [openGroup, setOpenGroup] = React.useState<any>(() => ROUTE_GROUP[route] || null);
  React.useEffect(() => { localStorage.setItem("fw_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  React.useEffect(() => { const g = ROUTE_GROUP[route]; if (g) setOpenGroup(g); }, [route]);

  const user = (auth && auth.user) || null;
  const displayName = user ? user.name : "Guest";
  const initial = (displayName || "U").trim().charAt(0).toUpperCase();
  // overdue / due-today follow-ups → red count on the To-Do nav item
  const todoUrgent = store ? todoUrgentCount(store) : 0;
  // platform owner → the FW Admin group at the bottom (support inbox, ops,
  // deletion requests, print orders). The unread count rides on Support Inbox.
  const supportAdmin = useSupportAdmin();
  // the service lens (lib/verticals.ts): App resolves it; the switch itself
  // shows only to a platform admin on a workspace with more than one service,
  // outside the sandbox — everyone else has today's fence sidebar, exactly
  const lens = (store && store.lens) || "fence";
  const V = VERTICALS[lens] || VERTICALS.fence;
  const enabledLenses = enabledVerticals(store && store.settings, supportAdmin && supportAdmin.verticals);
  const sandbox = !!((window as any).VFP_DEMO && (window as any).VFP_DEMO.active);
  const showSwitch = canSwitch(enabledLenses, !!(supportAdmin && supportAdmin.admin), sandbox);
  const withSupport = supportAdmin && supportAdmin.admin
    ? [...SIDEBAR_NAV, { ...FW_ADMIN_GROUP, children: FW_ADMIN_GROUP.children.map((c: any) => c.supportBadge ? { ...c, badge: supportAdmin.inboxUnread } : c) }]
    : SIDEBAR_NAV;
  /* hide what this role can't use: an entry with a capability it lacks, and any
     group left with no visible children. An employee is meant to open the app
     and see the few things they actually do. */
  const role = roleOf(store);
  // …and what the lens hides: fence-only screens under the Concrete lens
  // (nothing is hidden under the fence lens — the fixture test pins that)
  const hiddenHere = (it: any) => !!it.id && (routeHidden(it.id, lens) || (it.fenceOnly && lens !== "fence"));
  const visible = withSupport
    .filter((it: any) => (!it.cap || can(role, it.cap)) && !hiddenHere(it))
    .map((it: any) => (it.children ? { ...it, children: it.children.filter((c: any) => (!c.cap || can(role, c.cap)) && !hiddenHere(c)) } : it))
    .filter((it: any) => !it.children || it.children.length > 0);
  const nav = visible.filter((it: any, i: number) => !it.heading || (visible[i + 1] && visible[i + 1].id));
  const bottom = SIDEBAR_BOTTOM.filter((it: any) => !it.cap || can(role, it.cap));

  function isActive(item) {
    if (item.shortcut) return false;   // deep-link shortcuts (e.g. Team) never own the highlight
    if (item.also && item.also.includes(route)) return true;   // a page reached from this one (Customer Hub ← Customers)
    if (item.id !== route) return false;
    if (item.section) return section === item.section;
    // a plain entry for a sectioned route shouldn't light up while one of its
    // sectioned siblings is active (Estimator Widget vs Yard Preview, AI etc.)
    return !section;
  }
  function clickItem(item) {
    // "Help" opens the chat bubble (straight to the founder) rather than a page
    if (item.id === "help") { window.dispatchEvent(new Event("fw:help-open")); setMobileOpen && setMobileOpen(false); return; }
    go(item.id, item.params || (item.section ? { section: item.section } : null));
    setMobileOpen && setMobileOpen(false);
  }
  function toggleGroup(g, e?: any) {
    // a mouse click leaves the head focused, and the focus ring then reads as
    // "selected" while some other page is showing — drop it; keyboard users
    // still get the ring because focus-visible re-arms on the next Tab
    try { if (e && e.currentTarget && e.detail > 0) e.currentTarget.blur(); } catch (err) {}
    if (eff) { setCollapsed(false); setOpenGroup(g); return; }
    setOpenGroup((cur: any) => (cur === g ? null : g));
  }
  const canEstimate = can(role, "estimates");

  return (
    <aside className={"sidebar" + (eff ? " collapsed" : "") + (mobileOpen ? " mobile-open" : "")}>
      <div className="sb-top">
        <div className="sb-logo" onClick={() => go("dashboard")} title="FenceWorks">
          <LogoMark size={28} />
          {!eff && <span className="word"><b>Fence<span style={{ color: "var(--teal)" }}>Works</span></b></span>}
        </div>
        {isMobile
          ? <button className="sb-collapse" onClick={() => setMobileOpen && setMobileOpen(false)} aria-label="Close menu" title="Close"><Icon name="x" size={16} /></button>
          : <button className="sb-collapse" onClick={() => setCollapsed(c => !c)} title={eff ? "Expand" : "Collapse"} aria-label={eff ? "Expand sidebar" : "Collapse sidebar"}>
              <Icon name="chevright" size={16} style={{ transform: eff ? "none" : "rotate(180deg)" }} />
            </button>}
      </div>

      {/* the service switch: Fence | Concrete — platform admins, two-service workspaces only */}
      {showSwitch && <ServiceSwitcher enabled={enabledLenses} lens={lens} collapsed={eff} onPick={(id: any) => writeLensPref(id)} />}

      {/* the one primary action: draw a fence, get a price */}
      {canEstimate && (
        <div className="sb-cta">
          <button className={"btn btn-primary sb-new" + (route === "estimator" ? " on" : "")} onClick={() => clickItem({ id: "estimator" })} title={V.newEstimate} aria-label={V.newEstimate}>
            <Icon name="plus" size={16} />{!eff && <span>{V.newEstimate}</span>}
          </button>
        </div>
      )}

      <nav className="sb-nav">
        {nav.map((item, i) => {
          if (item.heading) return <div key={"h" + i} className="sb-heading" aria-hidden={eff ? "true" : undefined}>{!eff && <span>{item.heading}</span>}</div>;
          if (item.group) {
            const groupActive = ROUTE_GROUP[route] === item.group;
            const isOpen = openGroup === item.group && !eff;
            // a closed group still shows the unread count its children carry
            const groupBadge = !isOpen ? (item.children || []).reduce((n: number, c: any) => n + (c.badge > 0 ? c.badge : 0), 0) : 0;
            return (
              <div key={item.group} className={"sb-group" + (item.admin ? " sb-admin" : "")}>
                <button className={"sb-item sb-grouphead" + (groupActive && eff ? " active" : "") + (isOpen ? " open" : "") + (groupActive && !isOpen ? " holds" : "")} aria-expanded={isOpen} onClick={(e) => toggleGroup(item.group, e)} title={item.admin ? item.label + " — platform owner only" : item.label}>
                  <span className="sb-ico-wrap"><Icon name={item.icon} size={18} className="sb-ico" />{groupBadge > 0 && <span className="sb-badge">{groupBadge > 9 ? "9+" : groupBadge}</span>}</span>
                  {!eff && <span className="sb-label">{item.label}</span>}
                  {!eff && <Icon name="chevdown" size={14} className="sb-caret" style={{ transform: isOpen ? "none" : "rotate(-90deg)" }} />}
                </button>
                {isOpen && (
                  <div className="sb-children">
                    {item.children.map((c, k) => (
                      <button key={c.id + (c.section || "") + k} className={"sb-subitem" + (isActive(c) ? " active" : "")} onClick={() => clickItem(c)}>
                        <span className="sb-subdot" />
                        <span className="sb-label">{c.label}</span>
                        {c.beta && <span className="beta">BETA</span>}
                        {c.badge > 0 && <span className="sb-badge" style={{ position: "static", marginLeft: "auto" }}>{c.badge > 9 ? "9+" : c.badge}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <button key={item.id} className={"sb-item" + (isActive(item) ? " active" : "")} onClick={() => clickItem(item)} title={item.label}>
              <span className="sb-ico-wrap"><Icon name={item.icon} size={18} className="sb-ico" />{item.unreadDot && unread && unread.count > 0 && <span className="sb-badge">{unread.count > 9 ? "9+" : unread.count}</span>}{item.todoBadge && todoUrgent > 0 && <span className="sb-badge">{todoUrgent > 9 ? "9+" : todoUrgent}</span>}</span>
              {!eff && <span className="sb-label">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      <div className="sb-foot">
        {/* Settings (one destination, tabs inside) and the guide */}
        <div className="sb-bottom">
          {bottom.map((item: any) => (
            <button key={item.id} className={"sb-item sb-bottom-item" + (isActive(item) ? " active" : "")} onClick={() => clickItem(item)} title={item.label}>
              <Icon name={item.icon} size={16} className="sb-ico" />{!eff && <span className="sb-label">{item.label}</span>}
            </button>
          ))}
        </div>
        {/* the person: name + plan, the account things that aren't company
            settings (billing, team) as small links, and sign out as one icon */}
        <div className={"sb-user" + (route === "account" ? " on" : "")}>
          <div className="avatar" title={displayName + " · Account & billing"} onClick={() => clickItem({ id: "account" })} style={{ cursor: "pointer" }}>{initial}</div>
          {!eff && (
            <div className="sb-user-meta">
              <div className="nm" title={displayName + " · " + planLabel} onClick={() => clickItem({ id: "account" })} style={{ cursor: "pointer" }}>{displayName}</div>
              <div className="sb-user-links">
                {can(role, "billing")
                  ? <button onClick={() => clickItem({ id: "account" })} title={planLabel + " · Account & billing"}>{(user ? planLabel : "Free plan").replace(" plan", "")} · Billing</button>
                  : <span className="sb-plan">{user ? roleLabel(roleOf(store)) + " · " + planLabel : "Free plan"}</span>}
                {can(role, "team") && <button onClick={() => clickItem({ id: "account", params: { tab: "team" } })}>Team</button>}
              </div>
            </div>
          )}
          {!eff && <button className="sb-signout" title={(window as any).VFP_DEMO && (window as any).VFP_DEMO.active ? "Leave sandbox" : "Sign out"} aria-label={(window as any).VFP_DEMO && (window as any).VFP_DEMO.active ? "Leave sandbox" : "Sign out"} onClick={() => { const d: any = (window as any).VFP_DEMO; if (d && d.active) return d.exit(); auth && auth.signOut && auth.signOut(); }}><Icon name="x" size={14} /></button>}
        </div>
      </div>
    </aside>
  );
}

/* Fence | Concrete. A two-way segmented control under the logo; collapsed, one
   icon per service. Writes the per-device pick (lib/verticals.ts writeLensPref),
   which fires fw:vertical and App re-resolves the lens. */
function ServiceSwitcher({ enabled, lens, collapsed, onPick }: any) {
  if (collapsed) return (
    <div className="sb-switch collapsed" role="group" aria-label="Service" title={"Service: " + (VERTICALS[lens] || VERTICALS.fence).label}>
      {enabled.map((id: any) => (
        <button key={id} type="button" className={"sb-switch-dot" + (id === lens ? " on" : "")} aria-label={VERTICALS[id].label} aria-pressed={id === lens} title={VERTICALS[id].label} onClick={() => onPick(id)}>
          <Icon name={VERTICALS[id].icon} size={14} />
        </button>
      ))}
    </div>
  );
  return (
    <div className="sb-switch" role="group" aria-label="Service">
      <div className="seg" role="tablist" aria-label="Service">
        {enabled.map((id: any) => (
          <button key={id} type="button" role="tab" aria-selected={id === lens} className={"seg-btn" + (id === lens ? " on" : "")} onClick={() => onPick(id)}>
            <Icon name={VERTICALS[id].icon} size={12} />{VERTICALS[id].label}
          </button>
        ))}
      </div>
    </div>
  );
}

Object.assign(window, { Sidebar, SIDEBAR_NAV, SIDEBAR_BOTTOM, ROUTE_GROUP, FW_ADMIN_GROUP, ServiceSwitcher });

export { Sidebar, SIDEBAR_NAV, SIDEBAR_BOTTOM, ROUTE_GROUP, FW_ADMIN_GROUP, ServiceSwitcher };
